import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CloudSnow } from '@phosphor-icons/react';
import { getWeatherAlerts } from '@/lib/api/routes/weather-alerts';
import type { WeatherForecastAlert } from '@/lib/api/routes/weather-alerts';
import { useAuthStatus } from '@/hooks/useAuthStatus';
import { format } from 'date-fns';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export function ForecastTimeline({ alert }: { alert: WeatherForecastAlert }) {
  const periods = alert.forecastPeriods;
  if (periods.length === 0) return <p className="text-xs text-stone-400 py-4">No forecast periods available.</p>;

  const maxInches = Math.max(...periods.map(p => p.snowfallInches));

  return (
    <div className="space-y-1.5">
      {periods.map((period, i) => {
        const start = new Date(period.startTime);
        const end = new Date(period.endTime);
        const pct = maxInches > 0 ? (period.snowfallInches / maxInches) * 100 : 0;
        const isZero = period.snowfallInches === 0;

        return (
          <div key={i} className="flex items-center gap-3 group">
            {/* Time label */}
            <div className="w-[110px] shrink-0 text-right">
              <span className="text-[11px] tabular-nums text-stone-500">
                {format(start, 'EEE h a')}
                <span className="text-stone-300 mx-0.5">–</span>
                {format(end, 'h a')}
              </span>
            </div>

            {/* Bar */}
            <div className="flex-1 h-5 bg-stone-100 rounded overflow-hidden relative">
              {!isZero && (
                <div
                  className="h-full rounded bg-primary/60 transition-all duration-500"
                  style={{ width: `${Math.max(pct, 4)}%` }}
                />
              )}
            </div>

            {/* Value */}
            <span className={`w-10 text-right text-xs tabular-nums font-medium ${isZero ? 'text-stone-300' : 'text-stone-700'}`}>
              {period.snowfallInches}"
            </span>
          </div>
        );
      })}
    </div>
  );
}

export function useWeatherAlerts() {
  const { isAuthenticated, inOrg } = useAuthStatus();

  const { data } = useQuery({
    queryKey: ['weather-alerts'],
    queryFn: () => getWeatherAlerts(),
    enabled: isAuthenticated && inOrg,
    refetchInterval: 15 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  });

  return data?.alerts || [];
}

export function WeatherAlertModal({ open, onOpenChange, alerts }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  alerts: WeatherForecastAlert[];
}) {
  const [selectedAreaId, setSelectedAreaId] = useState<string>('');

  const selectedAlert = alerts.find(a => a.serviceAreaId === selectedAreaId) || alerts[0];

  // Reset selection when opening
  useEffect(() => {
    if (open && alerts.length > 0 && !selectedAreaId) {
      setSelectedAreaId(alerts[0].serviceAreaId);
    }
  }, [open, alerts, selectedAreaId]);

  if (!alerts.length || !selectedAlert) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] p-0 gap-0 overflow-hidden">
        <DialogHeader variant="bordered">
          <DialogTitle className="flex items-center gap-2">
            <CloudSnow className="h-5 w-5 text-primary" />
            Snowfall Forecast
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            NWS forecast for the next {selectedAlert.forecastPeriods.length * 6} hours in 6-hour periods.
          </p>
        </DialogHeader>

        <DialogBody className="space-y-5">
          {/* Area selector */}
          {alerts.length > 1 && (
            <Select value={selectedAlert.serviceAreaId} onValueChange={setSelectedAreaId}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {alerts.map(a => (
                  <SelectItem key={a.serviceAreaId} value={a.serviceAreaId}>
                    {a.serviceAreaName}
                    <span className="text-stone-400 ml-1.5">· {a.forecastSnowfallInches}"</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Total */}
          <div className="flex items-baseline justify-between">
            <span className="text-[11px] uppercase tracking-wider text-stone-400 font-medium">
              Total accumulation
            </span>
            <span className="text-lg font-semibold tabular-nums text-stone-800">
              {selectedAlert.forecastSnowfallInches}"
            </span>
          </div>

          {/* Timeline */}
          <div className="bg-stone-50 rounded-lg border border-stone-100 p-3 max-h-[360px] overflow-y-auto">
            <ForecastTimeline alert={selectedAlert} />
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
